import { useQuery } from "@tanstack/react-query"
import { Handle, MarkerType, type Edge, type Node, type NodeProps, Position } from "@xyflow/react"
import { useMemo } from "react"
import { useT } from "@/lib/i18n"
import { layeredLayout } from "@/lib/layout"
import { asArray, asString, cn, isRecord } from "@/lib/utils"
import { GraphCanvas, declaredEdge } from "./graph/canvas"

type StateFlowNode = Node<{ state: string; initial: boolean; terminal: boolean }, "state">

function StateNode({ data, selected }: NodeProps<StateFlowNode>) {
  return (
    <div
      className={cn(
        "flex h-9 min-w-24 items-center justify-center rounded-full border bg-card px-4 font-mono text-xs text-foreground transition-[border-color,box-shadow] duration-150",
        data.initial ? "border-primary/70" : "border-border",
        data.terminal && "border-double border-[3px]",
        selected && "border-primary shadow-[0_0_0_3px_var(--primary-soft)]",
      )}
    >
      <Handle type="target" position={Position.Left} className="!opacity-0" />
      {data.state}
      <Handle type="source" position={Position.Right} className="!opacity-0" />
    </div>
  )
}

const nodeTypes = { state: StateNode }

export interface LifecycleModel {
  readonly states: readonly string[]
  readonly initial: ReadonlySet<string>
  readonly terminal: ReadonlySet<string>
  readonly transitions: readonly { readonly id: string; readonly from: string; readonly to: string }[]
}

/** States and transitions as the lifecycle declares them; states named only by transitions are included. */
export function lifecycleModel(lifecycle: Record<string, unknown>): LifecycleModel {
  const initial = new Set(asArray(lifecycle.initial).map(String))
  const terminal = new Set(asArray(lifecycle.terminal).map(String))
  const transitions = asArray(lifecycle.transitions)
    .filter(isRecord)
    .flatMap((t) => asArray(t.from).map((from) => ({ id: asString(t.id) ?? "?", from: String(from), to: asString(t.to) ?? "?" })))
  const states = [...new Set([...initial, ...transitions.flatMap((t) => [t.from, t.to]), ...terminal])]
  return { states, initial, terminal, transitions }
}

/** A read-only lifecycle diagram, labelled declared. */
export function LifecycleDiagram({ lifecycle, id }: { lifecycle: Record<string, unknown>; id: string }) {
  const t = useT()
  const model = useMemo(() => lifecycleModel(lifecycle), [lifecycle])
  const layout = useQuery({
    queryKey: ["layout:lifecycle", id, model.states.join("|"), model.transitions.length],
    queryFn: () =>
      layeredLayout(
        model.states.map((s) => ({ id: s, width: Math.max(96, s.length * 8 + 40), height: 36 })),
        model.transitions.map((t, i) => ({ id: `${i}`, source: t.from, target: t.to })),
      ),
    staleTime: Infinity,
  })
  const flow = useMemo(() => {
    const positions = layout.data
    if (!positions) return null
    const nodes: StateFlowNode[] = model.states.map((s) => ({ id: s, type: "state", position: positions.get(s) ?? { x: 0, y: 0 }, data: { state: s, initial: model.initial.has(s), terminal: model.terminal.has(s) } }))
    const edges: Edge[] = model.transitions.map((t, i) => ({
      id: `${t.id}:${t.from}:${i}`,
      source: t.from,
      target: t.to,
      label: t.id,
      type: "default",
      style: declaredEdge,
      markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: "var(--subtle-foreground)" },
    }))
    return { nodes, edges }
  }, [layout.data, model])
  return (
    <div className="h-96 overflow-hidden rounded-lg border border-border-subtle" aria-label={t("lifecycle.diagram", { count: model.states.length })}>
      {flow ? <GraphCanvas nodes={flow.nodes} edges={flow.edges} nodeTypes={nodeTypes} /> : null}
    </div>
  )
}
